import { Box } from "@chakra-ui/react";
import React from "react";
import {
  AlertIcon,
  CornerDownRightIcon,
  ErrorIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  SparklesIcon,
  SunshineIcon,
  TargetIcon,
  TrendIcon,
  TvIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/media-entertainment.png";
import { dataBreachStat, type CdpIndustryConfig } from "./shared";

export const mediaEntertainmentCdpConfig: CdpIndustryConfig = {
  icon: TvIcon,
  name: "media & entertainment",
  titleCaseName: "Media & Entertainment",
  pluralNoun: "media & entertainment companies",
  color: "violet",
  solutionPageSlug: "/solutions/media",
  seoCopy:
    "Leverage all your retail data, including subscribers, content items, viewership logs, advertising campaigns, licensing agreements, and engagement metrics.",
  heroImage: {
    src: hero,
    alt: "A photograph of people at a live music concert with a list of example data tables overlayed. The data tables included are for advertising campaigns, engagement metrics, subscribers, content items, and viewership.",
  },
  adoptingCdpsDescription: (
    <>
      Media and entertainment is an incredibly saturated market. You’re not just competing against your competitors;
      you’re competing for attention. Customers have endless choices for content, platforms, and experiences.
      <br />
      <br />
      The key to maximizing customer lifetime value and increasing customer retention is delivering relevant and
      consistent experiences that anticipate customer preferences. And the best way to differentiate is to curate
      personalized experiences that keep your audience loyal and active.
    </>
  ),
  featuredOutcomes: [
    {
      icon: MoneyIcon,
      color: "green",
      title: "Drive upsells",
    },
    {
      icon: TrendIcon,
      color: "blue",
      title: "Maximize LTV & ARPU",
    },
    {
      icon: RefreshIcon,
      color: "yellow",
      title: "Reduce churn",
    },
  ],
  dataStatsTitleMaxW: "3xl",
  dataStats: [
    {
      title: "$5T",
      description: (
        <>
          Estimated market size for
          <Box as="br" display={{ base: "block", lg: "none" }} /> media & entertainment by 2032
        </>
      ),
      href: "https://www.businessresearchinsights.com/market-reports/media-and-entertainment-market-102952",
    },
    {
      title: "41%",
      description: (
        <>
          Of viewers rely on
          <br />
          personalized recommendations
        </>
      ),
      href: "https://cloud.google.com/blog/products/media-entertainment/google-cloud-survey-finds-personalization-key-success-for-streaming-platforms",
    },
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title: "Netflix personalizes recommendations for 80% of all hours streamed through the platform",
      href: "https://dl.acm.org/doi/pdf/10.1145/2843948?download=true",
    },
    {
      title:
        "Spotify analyzes data from over 600M users to deliver a personalized “year-in-review” each year to every user",
      href: "/blog/how-spotify-wrapped-works",
    },
    {
      title: "YouTube generated over $30 billion from targeted advertising",
      href: "https://www.statista.com/statistics/289658/youtube-global-net-advertising-revenues/?utm_source=chatgpt.com",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new subscription sign-ups by using behavioral and interest data to target potential users and lookalike audiences with offers like free trials or exclusive content, expanding your subscriber base.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing active subscribers from acquisition campaigns using CRM data, reallocating budget toward lapsed or new users to maximize efficiency and ROAS.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who signed up for a trial but didn’t convert by serving reminders and incentives, recovering lost opportunities and increasing subscription conversions.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Promote premium add-ons or services by using viewing behavior to upsell features like ad-free experiences, family plans, or content bundles, increasing ARPU.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Media networks",
        description:
          "Monetize anonymized audience insights by creating targeted segments for advertising partnerships with entertainment brands, generating incremental revenue through premium data sales.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend premium content upgrades like ad-free plans, live sports, or family bundles to increase LTV.",
      },
      {
        icon: ErrorIcon,
        title: "Churn prevention campaigns",
        description:
          "Identify and re-engage subscribers who haven’t streamed recently by offering personalized content recommendations.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Use exclusive discounts or content previews to win back former subscribers and boost revenue.",
      },
      // Second row
      {
        icon: SunshineIcon,
        title: "Seasonal content campaigns",
        description: "Promote themed collections like holiday movies or seasonal blockbusters to increase engagement.",
      },
      {
        icon: HeartIcon,
        title: "Loyalty campaigns",
        description:
          "Reward long-term subscribers with early access to new shows, exclusive content, or discounts on premium plans.",
      },
      {
        icon: SparklesIcon,
        title: "Milestone celebrations",
        description: "Reward users for completing series or reaching engagement milestones to boost retention.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display trending shows, top picks, and personalized recommendations to increase engagement and drive more streaming hours.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Dynamic app experiences",
        description:
          "Simplify content discovery by prominently featuring favorite genres, personalized playlists, and unfinished episodes to improve retention and usability.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Tailor search results based on user preferences, highlighting new releases in frequently watched genres to increase search-to-view conversion rates.",
      },
      // Second row
      {
        icon: PersonWithCheckIcon,
        title: "Recommended playlists",
        description:
          "Use viewing history to curate personalized playlists or recommend similar content, encouraging longer streaming sessions and deeper engagement.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Suggest enabling offline downloads, joining watch parties, or setting up parental controls to drive feature adoption and improve user experience.",
      },
      {
        icon: AlertIcon,
        title: "Proactive reminders",
        description:
          "Re-engage users by sending alerts about unwatched episodes, expiring downloads, or upcoming releases to build excitement and boost retention.",
      },
      // Third row
      {
        icon: SparklesIcon,
        title: "Milestone celebrations",
        description:
          "Recognize engagement milestones such as total hours streamed or number of completed shows to strengthen emotional connections and encourage continued usage.",
      },
      {
        icon: TvIcon,
        title: "Live viewing party suggestions",
        description:
          "Promote shared experiences by recommending live viewing parties for major events like sports finals or series premieres to create community-driven engagement.",
      },
      {
        icon: PinIcon,
        title: "Local content recommendations",
        description:
          "Highlight regionally relevant programming or special events based on the user’s location to drive engagement through culturally relevant content.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional:
        "Uses predefined models that may not fully capture content consumption patterns or platform engagement",
      composable: "Supports tailored models to handle viewer histories, subscription tiers, and viewing preferences",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with categories like binge-watch behaviors or genre preferences",
      composable: "Enables granular segmentation using viewer habits, engagement metrics, and subscription types",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like trial conversions or new content announcements",
      composable:
        "Powers fully adaptable journeys for events like subscription renewals, cross-platform engagement, and churn prevention",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across streaming platforms and subscription services",
      composable:
        "Supports custom algorithms to unify customer profiles from streaming subscriptions and multi-device usage",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Streaming & subscription trial drop-offs",
    firstCategoryLabel: "Free trial cancellations",
    secondCategoryLabel: "Newsletter subscribers",
  },
};
